import { GoldenBonusSymbolTypes } from "@spawn/golden-bonus";
import { Page, PageHeadline, PageHeadlineSize, PagePaylines, PageSeparator, PageSymbols, useService } from "@spawn/modern-ui";
import { WinlineType, WinningsCalculationNote } from "@spawn/page-components";
import { CurrencyService, IPaytableValue, NetworkService, PaytableService } from "@spawn/slot-machine";
import * as React from "react";
import Url from "url-parse";
import { BaseSymbolTypes } from "../services/symbolService";
import { BonusSymbolsSection } from "./bonusSymbolsSection";
import { IPageHelpersFactory } from "./pageHelpers";

/** Creates the paytable page of a game. */
export const createPaytablePage = ({ assets, usePageHelpers }: IPageHelpersFactory): (() => JSX.Element) => {
	const PaytablePage = (): JSX.Element => {
		const {
			services: { i18n },
			components: { Headline, TextWithImages, Text, FeatureBanner },
			createImageUrl,
			createBonusSymbol,
		} = usePageHelpers();
		const paytableService = useService(PaytableService);
		const currencyService = useService(CurrencyService);
		const networkService = useService(NetworkService);

		const totalBet = currencyService.getLocalized(networkService.totalBet);
		const numberOfWinlines = networkService.activePaylineCount;

		const createSymbol = (
			symbolType: BaseSymbolTypes,
			filename: string
		): { symbolName: BaseSymbolTypes; symbolUrl: Url<string>; payoutValues: IPaytableValue[] } => ({
			symbolName: symbolType,
			symbolUrl: createImageUrl(filename),
			payoutValues: paytableService.getPaytableValues(symbolType),
		});

		return (
			<Page testId="paytable">
				<PageHeadline
					testId="general.settings.tabs.paytable"
					text={i18n.t("general.settings.tabs.paytable")}
					size={PageHeadlineSize.H1}
				/>

				<WinningsCalculationNote winlineType={WinlineType.Lines} totalBet={totalBet} numberOfWinlines={numberOfWinlines} />

				<Headline testId="nudgenreveal.headline" />
				<TextWithImages testId="nudgenreveal.text1" params={{ symbolId: "[reveal]" }} />
				<TextWithImages testId="nudgenreveal.text2" params={{ symbolId1: "[reveal]", symbolId2: "[golden_bonus]" }} />
				<TextWithImages testId="nudgenreveal.text12" params={{ IMAGE1: "[reveal]" }} />
				<TextWithImages testId="nudgenreveal.text3" params={{ symbolId: "[reveal]" }} />

				<PageSeparator />

				<Headline testId="nudgenreveal.headline2" />
				<FeatureBanner filename={assets.goldenBonusImage} />
				<TextWithImages
					testId="nudgenreveal.text10"
					params={{
						symbolId: "[golden_bonus]",
						symbollist: "[bonus_s], [bonus_m], [bonus_l], [bonus_xl], [scatter_fg]",
					}}
				/>

				<PageSeparator />

				<Headline testId="nudgenreveal.text4" />
				<FeatureBanner filename={assets.bonusBanner} />
				<BonusSymbolsSection
					isHorizontal={true}
					symbols={[
						createBonusSymbol(GoldenBonusSymbolTypes.BonusX2500, "bonus_xl.png", ["2500", "1000"]),
						createBonusSymbol(GoldenBonusSymbolTypes.BonusX500, "bonus_l.png", ["500", "250", "100"]),
						createBonusSymbol(GoldenBonusSymbolTypes.BonusX50, "bonus_m.png", ["50", "25", "10"]),
						createBonusSymbol(GoldenBonusSymbolTypes.BonusX5, "bonus_s.png", ["5", "2", "1"]),
					]}
				/>
				<Text testId="nudgenreveal.text11" />

				<PageSeparator />

				<Headline testId="wild.title" />
				<TextWithImages testId="wild.exceptScatterAndBonus" params={{ symbolId: "[top]" }} />

				<PageSeparator />

				<Headline testId="general.freegames" />
				<TextWithImages testId="scatter.isScatter" params={{ symbolId: "[scatter_bg]" }} />
				<TextWithImages testId="freegames.trigger3" params={{ scatterCount: "3", symbolId: "[scatter_bg]" }} />
				<TextWithImages testId="nudgenreveal.fg.extraScatter" params={{ symbolId: "[reveal]" }} />
				<TextWithImages testId="nudgenreveal.text5" params={{ symbolId: "[reveal]" }} />
				<TextWithImages testId="nudgenreveal.text6" params={{ symbolId1: "[scatter_fg]", symbolId2: "[reveal]" }} />
				<Text testId="nudgenreveal.text7" params={{ amount: "1" }} />
				<Text testId="nudgenreveal.text8" />
				<FeatureBanner filename={assets.freegameReelsBanner} />

				<PageSeparator />

				<Headline testId="general.paytable.payout" />
				<PageSymbols
					symbols={[
						createSymbol(BaseSymbolTypes.Top, "top.png"),
						createSymbol(BaseSymbolTypes.HV1, "hv1.png"),
						createSymbol(BaseSymbolTypes.HV2, "hv2.png"),
						createSymbol(BaseSymbolTypes.HV3, "hv3.png"),
						createSymbol(BaseSymbolTypes.HV4, "hv4.png"),
						createSymbol(BaseSymbolTypes.LV1, "lv1.png"),
						createSymbol(BaseSymbolTypes.LV2, "lv2.png"),
						createSymbol(BaseSymbolTypes.LV3, "lv3.png"),
						createSymbol(BaseSymbolTypes.LV4, "lv4.png"),
						createSymbol(BaseSymbolTypes.Scatter, "scatter_bg.png"),
					]}
				/>
				<PageSeparator />
				<PageHeadline testId="helppage.paylines" text={i18n.t("helppage.paylines").toUpperCase()} />
				<PagePaylines />
			</Page>
		);
	};
	return PaytablePage;
};
